import React, { useEffect, useState } from 'react';
import { FiMessageCircle, FiTrash2, FiFileText, FiHeadphones, FiVideo } from 'react-icons/fi';
import { confirm } from '@/lib/confirm';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import moment from 'moment';
import { useAuditorium } from '@/hooks/useAuditorium';
import { useParams } from 'react-router-dom';
import {
  LessonMessageContainer,
  LessonMessageActions,
  LessonMessage,
  LessonMessageContent,
  LessonMessageDateTime,
  LessonMessageInfo,
  LessonMessageName,
  LessonMessageUser,
  LessonMessageFileThumb,
  LessonMessageFile,
  LessonMessageFileContent,
  LessonMessageFileTitle,
  LessonMessageFileMeta
} from '@/components/custom/lesson';
import { Button } from '@/components/ui/button';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

export function AuditoriumViewCardMessage(props) {
  const playfulStudent = usePlayfulStudent();
  const { deleteMessage, setMessageIds } = useAuditorium();
  const { text = true, message, onlyFile, chats } = props;
  const [file, setFile] = useState();
  const id = useParams().auditoriumId;

  const isAdmin = !useCheckIncludeProfiles({ only: ['student'] });

  useEffect(() => {
    if (message?.['auditorium-files']?.length > 0) {
      setFile(message?.['auditorium-files']?.[0]);
    }
  }, []);

  const handleSendMessageIds = () => {
    const messageIds = {
      messageId: message.id,
      lessonId: message['auditorium-lesson']?.id,
      messageBody: message.body
    };
    setMessageIds(messageIds);
  };

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: 'Deseja mesmo excluir esta mensagem?',
      description: 'Esta ação não poderá ser desfeita',
      confirmText: 'Sim',
      cancelText: 'Não'
    });
    if (confirmed) {
      deleteMessage(id, message, onlyFile, chats);
    }
  };

  const icon = file => {
    const splitFilename = file.filename.split('.');
    const extension = splitFilename[splitFilename.length - 1];
    switch (true) {
      case ['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'mpeg', '3gp', 'webm'].includes(extension):
        return (
          <LessonMessageFileThumb>
            <FiVideo />
          </LessonMessageFileThumb>
        );
      case ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'aiff', 'm4a', 'alac', 'opus'].includes(extension):
        return (
          <LessonMessageFileThumb>
            <FiHeadphones />
          </LessonMessageFileThumb>
        );
      default:
        return (
          <LessonMessageFileThumb>
            <FiFileText />
          </LessonMessageFileThumb>
        );
    }
  };

  return (
    <LessonMessageContainer className={playfulStudent ? 'border border-neutral-100' : undefined}>
      {playfulStudent ? (
        <div className="flex w-28 grow flex-col gap-1.5">
          <div className="flex items-baseline gap-2">
            <span
              className="truncate text-sm font-extrabold text-neutral-900"
              title={message?.user?.name}
            >
              {message?.user?.name}
            </span>
            <span className="shrink-0 text-xs font-semibold text-neutral-400">
              <time datetime={moment(message?.['created-at']).format('HH:mm')}>{moment(message?.['created-at']).format('HH:mm')}</time>
            </span>
          </div>

          <div className="w-full break-words text-sm text-neutral-900">
            {text && <div dangerouslySetInnerHTML={{ __html: message?.body }} />}

            {file && (
              <div className="mt-2 flex items-center gap-3 rounded-xl bg-bright p-3">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {file.filename && icon(file)}
                  <div className="min-w-0 flex-1">
                    <p className="mb-0 truncate text-xs font-bold text-neutral-900">{file?.filename}</p>
                    <p className="mb-0 truncate text-xs text-neutral-500">{`Prof. ${message.user.name} em ${moment(message?.['created-at']).format('DD/MM/YYYY')}`}</p>
                  </div>
                </div>
                <PlayfulCardButton
                  className="w-auto shrink-0 px-5 py-2 text-xs"
                  onClick={() => window.open(file?.file.url, '_blank', 'noopener,noreferrer')}
                >
                  Baixar
                </PlayfulCardButton>
              </div>
            )}

            {onlyFile && (
              <div className="flex items-center gap-3 rounded-xl bg-bright p-3">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {message?.filename && icon(message)}
                  <div className="min-w-0 flex-1">
                    <p className="mb-0 truncate text-xs font-bold text-neutral-900">{message.filename}</p>
                    <p className="mb-0 truncate text-xs text-neutral-500">{`Prof. ${message.user?.name ?? ''} em ${moment(message?.['created-at']).format('DD/MM/YYYY')}`}</p>
                  </div>
                </div>
                <PlayfulCardButton
                  className="w-auto shrink-0 px-5 py-2 text-xs"
                  onClick={() => window.open(message.file.url, '_blank', 'noopener,noreferrer')}
                >
                  Baixar
                </PlayfulCardButton>
              </div>
            )}
          </div>
        </div>
      ) : (
        <LessonMessage>
          <LessonMessageUser>
            {/* <Avatar
            className="avatar--xs"
            src={message?.user?.image?.url}
          /> */}
            <LessonMessageInfo>
              <LessonMessageName title={message?.user?.name}>{message?.user?.name}</LessonMessageName>
              <LessonMessageDateTime>
                <time datetime={moment(message?.['created-at']).format('HH:mm')}>{moment(message?.['created-at']).format('HH:mm')}</time>
              </LessonMessageDateTime>
            </LessonMessageInfo>
          </LessonMessageUser>

          <LessonMessageContent>
            {text && <div dangerouslySetInnerHTML={{ __html: message?.body }} />}

            {file && (
              <LessonMessageFile>
                {file.filename && icon(file)}
                <LessonMessageFileContent>
                  <LessonMessageFileTitle>{file?.filename}</LessonMessageFileTitle>
                  <LessonMessageFileMeta>{`Prof. ${message.user.name} em ${moment(message?.['created-at']).format('DD/MM/YYYY')}`}</LessonMessageFileMeta>
                </LessonMessageFileContent>
                <Button
                  variant="outline"
                  size="sm"
                  wide
                  asChild
                >
                  <a
                    href={file?.file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Baixar
                  </a>
                </Button>
              </LessonMessageFile>
            )}

            {onlyFile && (
              <LessonMessageFile>
                {message?.filename && icon(message)}
                <LessonMessageFileContent>
                  <LessonMessageFileTitle>{message.filename}</LessonMessageFileTitle>
                  <LessonMessageFileMeta>{`Prof. ${message.user?.name ?? ''} em ${moment(message?.['created-at']).format('DD/MM/YYYY')}`}</LessonMessageFileMeta>
                </LessonMessageFileContent>
                <Button
                  variant="outline"
                  size="sm"
                  wide
                  asChild
                >
                  <a
                    href={message.file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Baixar
                  </a>
                </Button>
              </LessonMessageFile>
            )}
          </LessonMessageContent>
        </LessonMessage>
      )}

      {(!onlyFile || isAdmin) && (
        <LessonMessageActions>
          {!onlyFile && (
            <FiMessageCircle
              className="text-primary"
              onClick={handleSendMessageIds}
            />
          )}

          {isAdmin && (
            <FiTrash2
              className="text-danger"
              onClick={handleDelete}
            />
          )}
        </LessonMessageActions>
      )}
    </LessonMessageContainer>
  );
}
